// ResetPasswordPage — F8.7b · consumes ?token=xxx from Resend email link.
//
// Route: /auth/reset-password?token=xxx
// Backend: POST /api/auth/reset-password { token, new_password }
// On success: toast + navigate to `/?login=success` (triggers modal open).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";
import { useAuthModal } from "@/context/AuthModalContext";

function passwordStrength(pw) {
    if (!pw) return 0;
    let s = 0;
    if (pw.length >= 8) s += 1;
    if (pw.length >= 12) s += 1;
    if (/[A-Z]/.test(pw)) s += 1;
    if (/[0-9]/.test(pw)) s += 1;
    if (/[^A-Za-z0-9]/.test(pw)) s += 1;
    return Math.min(s, 4);
}

export default function ResetPasswordPage() {
    const { t } = useTranslation("landing_v2");
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { openModal } = useAuthModal();
    const token = params.get("token") || "";

    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = "var(--color-bg)";
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    const strength = useMemo(() => passwordStrength(pw), [pw]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!token) { setError(t("auth.modal.error_generic")); return; }
        if (pw.length < 8) { setError(t("auth.modal.password_hint")); return; }
        if (pw !== pw2) { setError(t("auth.reset.mismatch")); return; }
        setSubmitting(true);
        try {
            const res = await fetch(`/api/auth/reset-password`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, new_password: pw }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (data.detail === "invalid_or_expired_token") {
                    setError(t("auth.reset.token_expired"));
                } else {
                    setError(t("auth.modal.error_generic"));
                }
                setSubmitting(false);
                return;
            }
            setDone(true);
            toast.success(t("auth.reset.success"));
            setTimeout(() => {
                navigate("/", { replace: true });
                openModal("login");
            }, 900);
        } catch {
            setError(t("auth.modal.error_generic"));
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bg text-text-main font-sans antialiased flex items-center justify-center p-6" data-testid="reset-password-root">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-[420px] bg-card-bg border border-brand-border rounded-2xl p-8 shadow-2xl"
            >
                <a href="/" className="flex items-center gap-2 mb-6" data-testid="reset-password-logo">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-black font-bold text-sm">
                        G
                    </div>
                    <span className="text-text-main font-semibold text-lg tracking-tight">Global Lead Suite</span>
                </a>

                <h1 className="text-text-main text-xl font-semibold tracking-tight mb-2">
                    {t("auth.reset.title")}
                </h1>
                <p className="text-text-secondary text-sm mb-6">
                    {t("auth.reset.sub")}
                </p>

                {!token && (
                    <div className="text-sm text-brand-alert mb-4" data-testid="reset-password-missing-token">
                        {t("auth.reset.missing_token")}
                    </div>
                )}

                {done ? (
                    <div className="text-center space-y-3 py-4" data-testid="reset-password-done">
                        <div className="w-12 h-12 mx-auto rounded-full bg-brand-success/15 border border-brand-success/40 flex items-center justify-center">
                            <svg className="w-6 h-6 text-brand-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p className="text-sm text-text-secondary">{t("auth.reset.success")}</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                        <div>
                            <label htmlFor="reset-pw" className="block text-xs text-text-secondary mb-1.5">
                                {t("auth.reset.new_password_label")}
                            </label>
                            <input
                                id="reset-pw"
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={8}
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                placeholder={t("auth.modal.password_placeholder")}
                                data-testid="reset-password-new"
                                className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                            />
                            {pw && (
                                <div className="mt-2 flex items-center gap-2">
                                    <div className="flex-1 h-1 bg-brand-border rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all ${
                                                strength <= 1 ? "bg-brand-alert w-1/4"
                                                : strength === 2 ? "bg-neutral-500 w-2/4"
                                                : strength === 3 ? "bg-brand-secondary w-3/4"
                                                : "bg-brand-success w-full"
                                            }`}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label htmlFor="reset-pw2" className="block text-xs text-text-secondary mb-1.5">
                                {t("auth.reset.confirm_password_label")}
                            </label>
                            <input
                                id="reset-pw2"
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={8}
                                value={pw2}
                                onChange={(e) => setPw2(e.target.value)}
                                placeholder={t("auth.modal.password_placeholder")}
                                data-testid="reset-password-confirm"
                                className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                            />
                        </div>
                        {error && (
                            <div className="text-xs text-brand-alert" data-testid="reset-password-error">{error}</div>
                        )}
                        <button
                            type="submit"
                            disabled={submitting || !token}
                            data-testid="reset-password-submit"
                            className="w-full px-4 py-2.5 rounded-xl bg-white hover:bg-[#EDEDED] text-black font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? "…" : t("auth.reset.submit")}
                        </button>
                    </form>
                )}
            </motion.div>
        </div>
    );
}
